import { useEffect, useState } from "react";
import { Star, BadgeCheck } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";

const QUOTES = [
  {
    text: "The only service I trust with a 5am departure out of MIA. The car is simply there — silent, spotless, early.",
    name: "E. Whitmore",
    title: "Private Client, Palm Beach Island",
  },
  {
    text: "Our wedding car arrived ribboned at The Breakers with a chauffeur who anticipated everything before we thought to ask.",
    name: "The Hartleys",
    title: "Wedding, Palm Beach",
  },
  {
    text: "Miami to Palm Beach, eleven meetings, one roadshow. Flawless to the minute. AUREUS is now on retainer.",
    name: "M. Okafor",
    title: "CFO, Brickell",
  },
];

export function Testimonials() {
  const [clientReviews, setClientReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setClientReviews(r.data || [])).catch(() => {});
  }, []);

  return (
    <section id="experience" className="relative py-28 lg:py-36 bg-[#16181D]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>Word of Mouth</Overline>
          <h2 className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl text-amber-50 mt-4 max-w-2xl leading-tight">
            Reputation, <span className="gold-text italic">whispered.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
          {QUOTES.map((q, i) => (
            <Reveal key={q.name} delay={i * 0.12}>
              <figure
                data-testid={`testimonial-card-${i}`}
                className="h-full border border-[#2E3340] bg-[#1E2128] p-8 lg:p-10 flex flex-col justify-between hover:border-[#C5A059]/60 transition-colors duration-500"
              >
                <div>
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={12} className="fill-amber-500 text-amber-500" />
                    ))}
                  </div>
                  <blockquote className="font-serif-lux text-xl lg:text-2xl text-amber-100/90 italic leading-relaxed">
                    “{q.text}”
                  </blockquote>
                </div>
                <figcaption className="mt-8 pt-6 border-t border-[#2E3340]">
                  <p className="font-mono-lux text-[11px] uppercase tracking-[0.25em] text-amber-400">{q.name}</p>
                  <p className="font-mono-lux text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{q.title}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        {clientReviews.length > 0 && (
          <div className="mt-16" data-testid="client-reviews">
            <Reveal>
              <p className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.35em] text-zinc-500">
                From recent journeys — verified clients
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              {clientReviews.slice(0, 3).map((r, i) => (
                <Reveal key={r.id} delay={i * 0.12}>
                  <figure
                    data-testid={`client-review-${r.id}`}
                    className="h-full border border-[#2E3340] bg-[#16181D] p-8 flex flex-col justify-between hover:border-[#C5A059]/60 transition-colors duration-500"
                  >
                    <div>
                      <div className="flex gap-1 mb-5">
                        {[...Array(5)].map((_, s) => (
                          <Star
                            key={s}
                            size={12}
                            className={s < r.rating ? "fill-amber-500 text-amber-500" : "text-zinc-700"}
                          />
                        ))}
                      </div>
                      <blockquote className="font-serif-lux text-lg text-amber-100/90 italic leading-relaxed">
                        “{r.text}”
                      </blockquote>
                    </div>
                    <figcaption className="mt-6 pt-5 border-t border-[#2E3340] flex items-center justify-between gap-3">
                      <p className="font-mono-lux text-[11px] uppercase tracking-[0.25em] text-amber-400">{r.name}</p>
                      <span className="inline-flex items-center gap-1.5 font-mono-lux text-[9px] uppercase tracking-[0.15em] text-emerald-400">
                        <BadgeCheck size={12} /> Verified
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
